import React, { useEffect, useMemo, useState } from "react";

import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import SendRoundedIcon from "@material-ui/icons/SendRounded";
import StarsRoundedIcon from "@material-ui/icons/StarsRounded";

import api from "../../../services/api";
import { useSetup } from "../hooks/useSetup";

import {
  KoraxOnboardingShell,
  KoraxOnboardingContent,
  KoraxOnboardingFooter
} from "../../../components/KoraxUI/Onboarding";

const useStyles = makeStyles(theme => ({
  layout: {
    width: "100%",
    maxWidth: 940,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 280px",
    gap: 18,
    alignItems: "stretch",

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  chat: {
    minHeight: 500,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.02)"
        : "#FFFFFF",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 18px 40px rgba(0,0,0,.18)"
        : "0 18px 40px rgba(15,23,42,.06)"
  },

  chatHeader: {
    padding: "15px 18px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    borderBottom: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "rgba(248,250,252,.92)"
  },

  avatar: {
    width: 42,
    height: 42,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 13,
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: 900,
    background: "linear-gradient(135deg,#155EEF,#123E9C)"
  },

  contact: {
    minWidth: 0
  },

  contactName: {
    overflow: "hidden",
    color: theme.palette.text.primary,
    fontSize: 13.5,
    fontWeight: 800,
    lineHeight: 1.3,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  online: {
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#16A34A",
    fontSize: 11.5,
    fontWeight: 600
  },

  onlineDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#22C55E"
  },

  messages: {
    flex: 1,
    minHeight: 330,
    maxHeight: 390,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 11,
    overflowY: "auto",
    background:
      theme.palette.type === "dark"
        ? "radial-gradient(circle at top right,rgba(21,94,239,.08),transparent 35%)"
        : "radial-gradient(circle at top right,rgba(21,94,239,.05),transparent 35%),#F8FAFC"
  },

  message: {
    maxWidth: "78%",
    padding: "11px 14px",
    borderRadius: 15,
    fontSize: 12.5,
    lineHeight: 1.55,
    wordBreak: "break-word"
  },

  aiMessage: {
    alignSelf: "flex-start",
    borderBottomLeftRadius: 5,
    color: theme.palette.text.primary,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#FFFFFF",
    border:
      theme.palette.type === "dark"
        ? "none"
        : "1px solid rgba(15,23,42,.07)"
  },

  userMessage: {
    alignSelf: "flex-end",
    borderBottomRightRadius: 5,
    color: "#FFFFFF",
    background: "#155EEF"
  },

  typing: {
    alignSelf: "flex-start",
    padding: "13px 15px",
    display: "flex",
    alignItems: "center",
    gap: 5,
    borderRadius: 15,
    borderBottomLeftRadius: 5,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#FFFFFF",

    "& span": {
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: theme.palette.text.secondary,
      animation: "$typingAnimation 1s infinite ease-in-out"
    },

    "& span:nth-child(2)": {
      animationDelay: ".15s"
    },

    "& span:nth-child(3)": {
      animationDelay: ".3s"
    }
  },

  "@keyframes typingAnimation": {
    "0%, 60%, 100%": {
      opacity: .4,
      transform: "translateY(0)"
    },

    "30%": {
      opacity: 1,
      transform: "translateY(-4px)"
    }
  },

  inputArea: {
    padding: 13,
    display: "flex",
    alignItems: "flex-end",
    gap: 9,
    borderTop: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF"
  },

  textarea: {
    flex: 1,
    minHeight: 44,
    maxHeight: 100,
    boxSizing: "border-box",
    resize: "none",
    padding: "12px 14px",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    outline: "none",
    color: theme.palette.text.primary,
    background:
      theme.palette.type === "dark"
        ? "rgba(0,0,0,.12)"
        : "#F8FAFC",
    fontFamily: "inherit",
    fontSize: 12.5,
    lineHeight: 1.45,

    "&:focus": {
      borderColor: "#155EEF",
      boxShadow: "0 0 0 3px rgba(21,94,239,.09)"
    },

    "&::placeholder": {
      color: theme.palette.text.secondary
    }
  },

  sendButton: {
    width: 44,
    height: 44,
    flexShrink: 0,
    borderRadius: 12,
    color: "#FFFFFF",
    background: "#155EEF",

    "&:hover": {
      background: "#124BC0"
    },

    "&.Mui-disabled": {
      color: "rgba(255,255,255,.55)",
      background: "rgba(21,94,239,.42)"
    }
  },

  sendIcon: {
    fontSize: 19
  },

  sidebar: {
    display: "flex",
    flexDirection: "column",
    gap: 14,

    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },

  card: {
    padding: 18,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF"
  },

  cardIcon: {
    width: 40,
    height: 40,
    marginBottom: 13,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    color: "#155EEF",
    background: "rgba(21,94,239,.10)"
  },

  cardIconSvg: {
    fontSize: 21
  },

  cardTitle: {
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 800
  },

  cardText: {
    marginTop: 6,
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    lineHeight: 1.55
  },

  suggestions: {
    marginTop: 14,
    display: "grid",
    gap: 8
  },

  suggestion: {
    width: "100%",
    padding: "10px 11px",
    boxSizing: "border-box",
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    background: "transparent",
    textAlign: "left",
    fontFamily: "inherit",
    fontSize: 11,
    lineHeight: 1.4,
    cursor: "pointer",

    "&:hover": {
      color: theme.palette.text.primary,
      borderColor: "rgba(21,94,239,.45)",
      background: "rgba(21,94,239,.04)"
    }
  },

  status: {
    marginTop: 14,
    padding: 11,
    display: "flex",
    alignItems: "center",
    gap: 8,
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.4
  },

  statusPending: {
    color: "#155EEF",
    background: "rgba(21,94,239,.08)"
  },

  statusComplete: {
    color: "#15803D",
    background: "rgba(22,163,74,.09)"
  },

  statusIcon: {
    flexShrink: 0,
    fontSize: 18
  }
}));

export default function TestStep() {
  const classes = useStyles();

  const {
    company,
    ai,
    updateAI,
    nextStep,
    previousStep
  } = useSetup();

  const companyName =
    company?.name ||
    company?.companyName ||
    "sua empresa";

  const initialMessages = useMemo(
    () => [
      {
        id: 1,
        sender: "ai",
        text: `Olá! Seja bem-vindo à ${companyName}. Como posso ajudar você hoje?`
      }
    ],
    [companyName]
  );

  const [messages, setMessages] = useState(initialMessages);
  const [message, setMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [hasTested, setHasTested] = useState(Boolean(ai?.tested));
  const [sessionId, setSessionId] = useState(null);
  const [sessionError, setSessionError] = useState("");

  const promptId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return Number(params.get("promptId") || ai?.promptId || 0);
  }, [ai?.promptId]);

  useEffect(() => {
    console.log("[TESTSTEP] componente carregado", {
      promptId,
      aiPromptId: ai?.promptId,
      url: window.location.href
    });

    const createSession = async () => {
      if (!promptId) {
        console.log("[TESTSTEP] promptId ausente");
        setSessionError("Agente não identificado para iniciar o teste.");
        return;
      }

      try {
        console.log("[TESTSTEP] criando sessão", { promptId, companyName });

        const { data } = await api.post("/onboarding/session", {
          promptId,
          name: companyName
        });

        console.log("[TESTSTEP] sessão criada", data);
        setSessionId(data.sessionId);
        setSessionError("");
      } catch (error) {
        console.error("[TESTSTEP] erro ao criar sessão", error);
        setSessionError(
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Não foi possível iniciar a conversa com a IA."
        );
      }
    };

    createSession();
  }, [promptId, companyName]);

  const createResponse = text => {
    const normalizedText = text.toLowerCase();

    if (
      normalizedText.includes("preço") ||
      normalizedText.includes("valor") ||
      normalizedText.includes("quanto custa")
    ) {
      return "Claro. Para indicar a melhor opção, posso entender primeiro qual produto ou serviço você procura?";
    }

    if (
      normalizedText.includes("agendar") ||
      normalizedText.includes("horário") ||
      normalizedText.includes("agenda")
    ) {
      return "Perfeito. Posso ajudar com o agendamento. Qual dia e período são melhores para você?";
    }

    if (
      normalizedText.includes("produto") ||
      normalizedText.includes("serviço") ||
      normalizedText.includes("catálogo")
    ) {
      return ai?.mainProducts
        ? `Trabalhamos principalmente com: ${ai.mainProducts}. Qual dessas opções chamou mais sua atenção?`
        : "Temos algumas opções disponíveis. Me conte o que você procura para eu orientar melhor.";
    }

    if (
      normalizedText.includes("oi") ||
      normalizedText.includes("olá") ||
      normalizedText.includes("bom dia") ||
      normalizedText.includes("boa tarde") ||
      normalizedText.includes("boa noite")
    ) {
      return "Olá! É um prazer falar com você. O que você gostaria de conhecer melhor?";
    }

    return "Entendi. Para ajudar da melhor forma, você pode me contar um pouco mais sobre o que precisa?";
  };

  const handleSend = async () => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage || isTyping || !sessionId) {
      return;
    }

    const userMessage = {
      id: Date.now(),
      sender: "user",
      text: trimmedMessage
    };

    setMessages(current => [...current, userMessage]);
    setMessage("");
    setIsTyping(true);
    setHasTested(true);

    try {
      const { data } = await api.post(
        `/onboarding/chat/${sessionId}`,
        { message: trimmedMessage }
      );

      console.log("[TESTSTEP] backend response", data);
      console.log("[TESTSTEP] messages", data?.messages);

      const replies = Array.isArray(data.messages)
        ? data.messages
        : [];

      for (let index = 0; index < replies.length; index += 1) {
        const reply = replies[index];

        const normalizedReply = {
          id: reply?.id || Date.now() + index + 1,
          sender: reply?.role === "user" ? "user" : "ai",
          type: reply?.type || "text",
          text:
            typeof reply === "string"
              ? reply
              : (reply?.text || reply?.caption || ""),
          mediaKey:
            typeof reply === "object"
              ? (reply?.mediaKey || null)
              : null,
          caption:
            typeof reply === "object"
              ? (reply?.caption || "")
              : ""
        };

        const responseLength = String(normalizedReply.text || "").length;
        const typingDelay =
          normalizedReply.type === "audio"
            ? 2200
            : Math.min(2800, Math.max(700, responseLength * 22));

        setIsTyping(true);

        await new Promise(resolve => {
          setTimeout(resolve, typingDelay);
        });

        setMessages(current => [
          ...current,
          normalizedReply
        ]);

        if (index < replies.length - 1) {
          setIsTyping(false);

          await new Promise(resolve => {
            setTimeout(resolve, 450);
          });
        }
      }
    } catch (err) {
      setMessages(current => [
        ...current,
        {
          id: Date.now() + 999,
          sender: "ai",
          text:
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Erro ao conversar com a IA."
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = event => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleContinue = () => {
    updateAI({
      tested: true
    });

    nextStep();
  };

  const suggestions = [
    {
      label: "“Gostaria de saber os valores.”",
      message: "Olá, gostaria de saber os valores."
    },
    {
      label: "“Quais serviços vocês oferecem?”",
      message: "Quais produtos ou serviços vocês oferecem?"
    },
    {
      label: "“Gostaria de agendar um horário.”",
      message: "Gostaria de agendar um horário."
    }
  ];

  return (
    <KoraxOnboardingShell step={6} totalSteps={7}>
      <KoraxOnboardingContent
        eyebrow="Experiência do cliente"
        title="Converse com sua IA"
        description="Faça um teste rápido para visualizar como será a experiência inicial dos seus clientes."
      >
        <div className={classes.layout}>
          <div className={classes.chat}>
            <div className={classes.chatHeader}>
              <div className={classes.avatar}>
                K
              </div>

              <div className={classes.contact}>
                <Typography className={classes.contactName}>
                  Assistente da {companyName}
                </Typography>

                <div className={classes.online}>
                  <span className={classes.onlineDot} />
                  Online agora
                </div>
              </div>
            </div>

            <div className={classes.messages}>
              {messages.map(item => (
                <div
                  key={item.id}
                  className={`${classes.message} ${
                    item.sender === "user"
                      ? classes.userMessage
                      : classes.aiMessage
                  }`}
                >
                  {item.text}
                </div>
              ))}

              {isTyping && (
                <div className={classes.typing}>
                  <span />
                  <span />
                  <span />
                </div>
              )}
            </div>

            <div className={classes.inputArea}>
              <textarea
                className={classes.textarea}
                rows={1}
                placeholder="Digite uma mensagem de teste..."
                value={message}
                onChange={event => setMessage(event.target.value)}
                onKeyDown={handleKeyDown}
              />

              <IconButton
                className={classes.sendButton}
                disabled={!message.trim() || isTyping}
                onClick={handleSend}
                aria-label="Enviar mensagem"
              >
                <SendRoundedIcon className={classes.sendIcon} />
              </IconButton>
            </div>
          </div>

          <aside className={classes.sidebar}>
            <div className={classes.card}>
              <div className={classes.cardIcon}>
                <StarsRoundedIcon className={classes.cardIconSvg} />
              </div>

              <Typography className={classes.cardTitle}>
                Teste algumas perguntas
              </Typography>

              <Typography className={classes.cardText}>
                Use mensagens parecidas com as que seus clientes enviam diariamente.
              </Typography>

              <div className={classes.suggestions}>
                {suggestions.map(suggestion => (
                  <button
                    key={suggestion.message}
                    type="button"
                    className={classes.suggestion}
                    onClick={() => setMessage(suggestion.message)}
                  >
                    {suggestion.label}
                  </button>
                ))}
              </div>

              <div
                className={`${classes.status} ${
                  hasTested
                    ? classes.statusComplete
                    : classes.statusPending
                }`}
              >
                <CheckCircleRoundedIcon className={classes.statusIcon} />

                {hasTested
                  ? "Teste realizado com sucesso."
                  : "Envie ao menos uma mensagem para continuar."}
              </div>
            </div>

            <div className={classes.card}>
              <Typography className={classes.cardTitle}>
                Ambiente de demonstração
              </Typography>

              <Typography className={classes.cardText}>
                Nesta etapa, as respostas são simuladas. A conexão com a IA real será feita depois da validação completa do onboarding.
              </Typography>
            </div>
          </aside>
        </div>
      </KoraxOnboardingContent>

      <KoraxOnboardingFooter
        onBack={previousStep}
        onNext={handleContinue}
        nextLabel="Finalizar configuração"
        disableNext={!hasTested}
      />
    </KoraxOnboardingShell>
  );
}
